import { CALENDAR_SYNC_URL, CALENDAR_SYNC_HEADERS } from '../../config.js'
import { rowToEvent } from '../../pages/calendar.js'
import { freshBooking } from '../../functions/calendar/calendarUtils.js'
export { CalOnlineAvatar, CalOnlineUsersBar } from '../OnlineUsersBar.js'

export const CalMain = {
  extends: 'AppMain',
  padding: '0',

  Header: { extends: 'CalHeader' },

  Body: {
    CalMonthView: {},
    CalWeekView: {},
    CalTeamView: {},
    CalTeamNowView: {}
  }
}

export const CalHeader = {
  extends: 'PageHeader',

  LeftGroup: {
    TodayBtn: {
      tag: 'button',
      text: 'Today',
      padding: 'Z A',
      round: 'Z',
      borderWidth: '1px',
      borderColor: 'line',
      borderStyle: 'solid',
      theme: 'secondary',
      cursor: 'pointer',
      fontSize: 'Z2',
      fontWeight: '500',
      onClick: (e, el, s) => {
        const t = new Date()
        s.root.update({
          currentYear: t.getFullYear(),
          currentMonth: t.getMonth(),
          currentDay: t.getDate()
        })
      }
    },

    NavGroup: {
      flow: 'x',
      childExtends: 'IconButton',

      PrevBtn: {
        Icon: { icon: 'arrowAngleLeft' },
        onClick: (e, el, s) => {
          const root = s.root
          if (root.viewMode === 'month') {
            const { year, month } = el.call(
              'prevMonth',
              root.currentYear,
              root.currentMonth
            )
            root.update({ currentYear: year, currentMonth: month })
          } else if (root.viewMode === 'team') {
            const d = new Date(
              root.currentYear,
              root.currentMonth,
              root.currentDay - 1
            )
            root.update({
              currentYear: d.getFullYear(),
              currentMonth: d.getMonth(),
              currentDay: d.getDate()
            })
          } else {
            const d = new Date(
              root.currentYear,
              root.currentMonth,
              root.currentDay - 7
            )
            root.update({
              currentYear: d.getFullYear(),
              currentMonth: d.getMonth(),
              currentDay: d.getDate()
            })
          }
        }
      },

      NextBtn: {
        Icon: { icon: 'arrowAngleRight' },
        onClick: (e, el, s) => {
          const root = s.root
          if (root.viewMode === 'month') {
            const { year, month } = el.call(
              'nextMonth',
              root.currentYear,
              root.currentMonth
            )
            root.update({ currentYear: year, currentMonth: month })
          } else if (root.viewMode === 'team') {
            const d = new Date(
              root.currentYear,
              root.currentMonth,
              root.currentDay + 1
            )
            root.update({
              currentYear: d.getFullYear(),
              currentMonth: d.getMonth(),
              currentDay: d.getDate()
            })
          } else {
            const d = new Date(
              root.currentYear,
              root.currentMonth,
              root.currentDay + 7
            )
            root.update({
              currentYear: d.getFullYear(),
              currentMonth: d.getMonth(),
              currentDay: d.getDate()
            })
          }
        }
      }
    },

    DateLabel: {
      tag: 'h2',
      fontSize: 'B1',
      fontWeight: '400',
      margin: '0',
      color: 'title',
      text: (el, s) => {
        const root = s.root
        if (root.viewMode === 'team-now') return 'Team Now'
        if (root.viewMode === 'team') {
          const d = new Date(
            root.currentYear,
            root.currentMonth,
            root.currentDay
          )
          return d.toLocaleDateString(undefined, {
            weekday: 'long',
            month: 'long',
            day: 'numeric',
            year: 'numeric'
          })
        }
        return el.call('getMonthLabel', root.currentYear, root.currentMonth)
      }
    },

    // Manual refetch — pokes the calendar-sync Edge Function (pull+push pass
    // against Google), then reactively re-fetches calendar_events from
    // Supabase and overwrites root.events in place. No page reload, no
    // browser cache to fight, the UI updates the moment the round-trip lands.
    ResyncBtn: {
      extends: 'IconButton',
      Icon: { icon: 'refresh' },
      title: 'Resync from Google',
      transform: (el, s) =>
        s.root.syncing ? 'rotate(360deg)' : 'rotate(0deg)',
      transition: 'transform 1s linear',
      onClick: async (e, el, s) => {
        const root = s.root
        if (root.syncing) return
        root.update({ syncing: true })
        try {
          await fetch(CALENDAR_SYNC_URL, {
            method: 'POST',
            headers: CALENDAR_SYNC_HEADERS
          })
        } catch (_) {
          /* sync down — still re-fetch so the user gets fresh DB rows */
        }
        try {
          // migrated 2026-04-27 to entity route workspaceProject.calendar
          const min = new Date(Date.now() - 30 * 86400000).toISOString()
          const max = new Date(Date.now() + 365 * 86400000).toISOString()
          const result = await el.context.fetch.select({
            from: 'workspaceProject.calendar',
            params: { deleted: false, start_at: { gte: min, lte: max } },
            order: 'start_at',
            limit: 5000
          })
          if (result?.data) {
            root.update({ events: result.data.map(rowToEvent) })
          }
        } catch (err) {
          console.error('[resync] re-fetch failed:', err)
        }
        root.update({ syncing: false })
      }
    }
  },

  CenterGroup: {
    // Driven by shared-gov's SharedViewSwitcher so calendar and chat
    // share the same segmented switcher chrome + click + active state
    // logic. Each child is a SharedViewSwitcherBtn keyed by `value`
    // which matches the parent's `stateKey` field on root state
    // (`viewMode`).
    ViewSwitcher: {
      extends: 'SharedViewSwitcher',
      childExtends: 'SharedViewSwitcherBtn',
      children: [
        { value: 'month', label: 'Month' },
        { value: 'week', label: 'Week' },
        { value: 'team', label: 'Team' },
        { value: 'team-now', label: 'Team Now' }
      ]
    }
  },

  RightGroup: {
    // OnlineBar/AiToggle/Profile already live in the global AppHeader.
    // Calendar-only action: New booking button.
    NewBookingBtn: {
      tag: 'button',
      flow: 'x',
      align: 'center center',
      gap: 'Z',
      padding: 'Z A',
      round: 'C',
      border: 'none',
      theme: 'primary',
      cursor: 'pointer',
      fontSize: 'Z2',
      fontWeight: '500',
      ':hover': { opacity: '.9' },
      onClick: (e, el, s) => {
        s.root.update({
          showNewBooking: true,
          newBooking: freshBooking({ date: el.call('todayISO') })
        })
      },
      Icon: { icon: 'plus' },
      Label: { tag: 'span', text: 'New booking' }
    }
  }
}

export const CalViewBtn = {
  tag: 'button',
  padding: 'Z A',
  border: 'none',
  cursor: 'pointer',
  fontSize: 'Z2'
}
